import { useState, useEffect, useCallback } from 'react';
import { TableTopExercise } from '../types/tabletopExercise';
import { getUserTabletopScenarios } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export interface TabletopNotificationState {
  showNotification: boolean;
  scenarios: TableTopExercise[];
  bcpName: string;
  isLoading: boolean;
  error: string | null;
}

export const useTabletopNotifications = () => {
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState<TabletopNotificationState>({
    showNotification: false,
    scenarios: [],
    bcpName: '',
    isLoading: false,
    error: null
  });

  const checkForNewScenarios = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      // GET /v1/tabletop-exercises?limit=50&offset=0 via api (services/api.ts) – must use HTTPS when page is HTTPS
      const scenarios = await getUserTabletopScenarios(50, 0);
      const recentScenarios = scenarios.filter((scenario: TableTopExercise) => {
        const createdAt = new Date(scenario.created_at);
        const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
        // Check if it's AI-generated (admin_approved = true) and not notified (iso_auditor_approved = false)
        return createdAt > oneDayAgo && scenario.admin_approved && !scenario.iso_auditor_approved;
      });

      if (recentScenarios.length > 0) {
        const firstScenario = recentScenarios[0];
        const bcpName = firstScenario?.bcp_id
          ? `BCP #${firstScenario.bcp_id}`
          : (firstScenario?.business_context?.business_name || 'your organization');
        setState(prev => ({
          ...prev,
          showNotification: true,
          scenarios: recentScenarios,
          bcpName,
          isLoading: false
        }));
      } else {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    } catch (error: any) {
      const isNetwork = error?.code === 'ERR_NETWORK' || error?.message === 'Network Error' || !error?.response;
      if (!isNetwork) console.error('Error checking for new tabletop scenarios:', error);
      setState(prev => ({
        ...prev,
        error: isNetwork ? null : (error instanceof Error ? error.message : 'Failed to check for new scenarios'),
        isLoading: false
      }));
    }
  }, []);

  const closeNotification = useCallback(() => {
    setState(prev => ({
      ...prev,
      showNotification: false,
      scenarios: [],
      bcpName: ''
    }));
  }, []);

  const markScenariosAsNotified = useCallback(async (scenarioIds: number[]) => {
    try {
      // This would typically call an API to mark scenarios as notified
      // For now, we'll just close the notification
      closeNotification();
    } catch (error) {
      console.error('Error marking scenarios as notified:', error);
    }
  }, [closeNotification]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    checkForNewScenarios();
  }, [checkForNewScenarios, isAuthenticated]);

  return {
    ...state,
    checkForNewScenarios,
    closeNotification,
    markScenariosAsNotified
  };
};
